import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  FlaskConical,
  HelpCircle,
  MoreVertical,
  X,
} from 'lucide-react';
import Menus from '../Menus';
import Section from './Section';
import Identity from './sections/Identity';
import Triggers from './sections/Triggers';
import ContextBlocks from './sections/ContextBlocks';
import ToolGrants from './sections/ToolGrants';
import ApprovalRules from './sections/ApprovalRules';
import SkillTestPanel from './SkillTestPanel';
import {
  useSkill,
  useCreateSkill,
  useUpdateSkill,
  useSaveSkillVersion,
  usePublishSkill,
} from '../../lib/skills.queries';
import type { SkillConfig } from '@netsuite-ai/types';
import { useIsMobile } from '../../hooks/useIsMobile';

const DEFAULT_CONFIG: SkillConfig = {
  identity: { name: '', description: '', whenToUse: '' },
  triggers: [],
  toolGrants: [],
  approvalRules: [],
  contextBlocks: [],
};

interface Props {
  skillId?: string;
  onBack: () => void;
  onSettingsClick?: () => void;
}

export default function SkillEditor({
  skillId: initialSkillId,
  onBack,
  onSettingsClick,
}: Props) {
  const [resolvedSkillId, setResolvedSkillId] = useState<string | null>(
    initialSkillId ?? null,
  );
  const [config, setConfig] = useState<SkillConfig>(DEFAULT_CONFIG);
  const [view, setView] = useState<'form' | 'json'>('form');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [showTest, setShowTest] = useState(false);

  const { data: existing } = useSkill(resolvedSkillId ?? '');
  const createSkill = useCreateSkill();
  const updateSkill = useUpdateSkill(resolvedSkillId ?? '');
  const saveVersion = useSaveSkillVersion();
  const publishSkill = usePublishSkill();

  useEffect(() => {
    if (!existing) return;
    const loaded: SkillConfig = existing.config ?? {
      ...DEFAULT_CONFIG,
      identity: {
        ...DEFAULT_CONFIG.identity,
        name: existing.skill.name,
        description: existing.skill.description,
      },
    };
    setConfig(loaded);
    // Keep jsonText in sync so switching to JSON view always shows current data
    setJsonText(JSON.stringify(loaded, null, 2));
  }, [existing]);

  function patch<K extends keyof SkillConfig>(key: K, value: SkillConfig[K]) {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }

  function handleSwitchToJson() {
    setJsonText(JSON.stringify(config, null, 2));
    setJsonError('');
    setView('json');
  }

  function handleSwitchToForm() {
    try {
      const parsed = JSON.parse(jsonText) as SkillConfig;
      setConfig(parsed);
      setJsonError('');
      setView('form');
    } catch {
      setJsonError('Invalid JSON — fix it before switching back');
    }
  }

  async function save(): Promise<{
    skillId: string;
    versionId: string;
  } | null> {
    let skillId = resolvedSkillId;

    if (!skillId) {
      const { skill } = await createSkill.mutateAsync({
        name: config.identity.name.trim(),
        description: config.identity.description.trim(),
      });
      skillId = skill.id;
      setResolvedSkillId(skillId);
    } else if (existing?.skill) {
      const { name, description } = config.identity;
      if (
        name !== existing.skill.name ||
        description !== existing.skill.description
      ) {
        await updateSkill.mutateAsync({ name, description });
      }
    }

    const { version } = await saveVersion.mutateAsync({
      skillId: skillId!,
      config,
    });
    return { skillId: skillId!, versionId: version.id };
  }

  async function handleSaveDraft() {
    await save();
    onBack();
  }

  async function handlePublish() {
    const result = await save();
    if (!result) return;
    await publishSkill.mutateAsync({
      skillId: result.skillId,
      versionId: result.versionId,
    });
    onBack();
  }

  const isMobile = useIsMobile();
  const isBusy =
    createSkill.isPending || saveVersion.isPending || publishSkill.isPending;
  const canSave = config.identity.name.trim().length > 0;

  return (
    <div className="flex h-full bg-white">
      {/* Left column: header, editor body, footer */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <div className="flex h-17.5 items-center justify-between gap-2 border-b border-neutral-200 px-4">
          <div className="flex gap-1">
            <button
              onClick={onBack}
              className="text-ink hover:bg-ink/8 -ml-1 rounded p-1"
              aria-label="Go back"
            >
              <ChevronLeft size={22} strokeWidth={2} />
            </button>
            <div className="flex flex-1 flex-col items-start">
              <p className="text-ink text-lg leading-tight font-bold">
                {resolvedSkillId
                  ? (existing?.skill.name ?? 'Edit Skill')
                  : 'New Skill'}
              </p>
              <button
                onClick={onSettingsClick}
                className="text-xs text-neutral-500 hover:underline"
              >
                Settings
              </button>
            </div>
          </div>

          <div>
            <button
              className="text-ink hover:bg-ink/8 rounded-full p-1"
              aria-label="Help"
            >
              <HelpCircle size={20} strokeWidth={1.5} />
            </button>
            <Menus>
              <Menus.Toggle
                id="skill-editor-menu"
                className="text-ink hover:bg-ink/8 rounded p-1"
              >
                <MoreVertical size={20} strokeWidth={1.5} />
              </Menus.Toggle>
              <Menus.List id="skill-editor-menu">
                <Menus.Button
                  icon={<FlaskConical size={14} />}
                  onClick={() => setShowTest((v) => !v)}
                >
                  {showTest ? 'Hide test panel' : 'Test skill'}
                </Menus.Button>
              </Menus.List>
            </Menus>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 border-b border-neutral-200 px-4 py-2">
          <button
            onClick={() => view === 'json' && handleSwitchToForm()}
            className={[
              'rounded-md px-3 py-1 text-xs font-medium',
              view === 'form'
                ? 'bg-neutral-100 text-neutral-900'
                : 'text-neutral-500 hover:bg-neutral-50',
            ].join(' ')}
          >
            Form
          </button>
          <button
            onClick={() => view === 'form' && handleSwitchToJson()}
            className={[
              'rounded-md px-3 py-1 text-xs font-medium',
              view === 'json'
                ? 'bg-neutral-100 text-neutral-900'
                : 'text-neutral-500 hover:bg-neutral-50',
            ].join(' ')}
          >
            JSON
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {view === 'json' ? (
              <div className="p-4">
                {jsonError && (
                  <p className="mb-2 text-xs text-red-500 dark:text-red-400">
                    {jsonError}
                  </p>
                )}
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  className="h-[calc(100vh-260px)] w-full resize-none rounded border border-neutral-300 p-3 font-mono text-xs outline-none focus:border-neutral-500"
                />
              </div>
            ) : (
              <div className="space-y-4 px-4 py-4">
                <Section number={1} title="Identity">
                  <Identity
                    value={config.identity}
                    onChange={(v) => patch('identity', v)}
                  />
                </Section>
                <Section number={2} title="Triggers">
                  <Triggers
                    value={config.triggers}
                    onChange={(v) => patch('triggers', v)}
                  />
                </Section>
                <Section number={3} title="Context blocks">
                  <ContextBlocks
                    value={config.contextBlocks}
                    onChange={(v) => patch('contextBlocks', v)}
                  />
                </Section>
                <Section number={4} title="Tool grants">
                  <ToolGrants
                    value={config.toolGrants}
                    onChange={(v) => patch('toolGrants', v)}
                  />
                </Section>
                <Section number={5} title="Approval rules">
                  <ApprovalRules
                    value={config.approvalRules}
                    onChange={(v) => patch('approvalRules', v)}
                  />
                </Section>
              </div>
            )}
          </div>
        </div>

        {/* Mobile: bottom sheet portal */}
        {isMobile &&
          showTest &&
          createPortal(
            <div
              className="fixed inset-0 z-50 flex items-end bg-black/60"
              onClick={() => setShowTest(false)}
            >
              <div
                className="flex h-[70vh] w-full flex-col bg-white"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mx-auto mt-3 mb-2 h-1 w-10 shrink-0 rounded-full bg-neutral-300" />
                <div className="flex shrink-0 items-center justify-between border-b border-neutral-200 px-4 pb-2">
                  <p className="text-sm font-semibold text-neutral-700">
                    Test panel
                  </p>
                  <button
                    onClick={() => setShowTest(false)}
                    className="rounded p-1 text-neutral-400 hover:text-neutral-600"
                    aria-label="Close test panel"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-hidden">
                  <SkillTestPanel
                    skillId={resolvedSkillId}
                    config={config}
                    hideHeader
                  />
                </div>
              </div>
            </div>,
            document.body,
          )}

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-neutral-200 px-4 py-3">
          <button
            onClick={onBack}
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveDraft}
            disabled={isBusy || !canSave}
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-40"
          >
            {saveVersion.isPending && !publishSkill.isPending
              ? 'Saving…'
              : 'Save draft'}
          </button>
          <button
            onClick={handlePublish}
            disabled={isBusy || !canSave}
            className="bg-danger rounded-md px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40"
          >
            {publishSkill.isPending ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Desktop: full-height right rail, level with the header */}
      {!isMobile && (
        <div
          className={`shrink-0 overflow-hidden border-l border-neutral-200 transition-[width] duration-200 ease-in-out ${
            showTest ? 'w-100' : 'w-0'
          }`}
        >
          <div className="h-full w-full">
            <SkillTestPanel
              skillId={resolvedSkillId}
              config={config}
              onClose={() => setShowTest(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
